import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchCourseDetails } from "../../redux/Actions/courseActions";
import CourseLevelCoverage from "../CourseLevelCoverage";
import JoinNowModalManager from "../Dashboard/StudentDashboard/JoinNowModalManager";
import Meta from "../Meta/Meta";
import { fetchPlacementTestStatus } from "./../../redux/Actions/studentPlacementActions";
import LoadingSpinner from "./../Loader/LoadingSpinner";
import BookingConfirmationModal from "./BookingConfirmationModal";
import CourseInfoBox from "./CourseInfoBox";
import courseTermsContent from "../Dashboard/StudentDashboard/courseTermsContent";
import api from "lib/api/api";
import TeacherFeedbackSection from "../TeacherFeedback/TeacherFeedbackSection";

const COURSE_TERMS_VERSION = "course-terms-v1";

const fallbackPlacementRules = [
  { minScore: 90, maxScore: 100, resultStatus: "PASS", allowBooking: true },
  { minScore: 75, maxScore: 89, resultStatus: "PASS_WITH_RECOMMENDATION", allowBooking: true },
  { minScore: 60, maxScore: 74, resultStatus: "CONDITIONAL_REVIEW", allowBooking: false },
  { minScore: 0, maxScore: 59, resultStatus: "FAIL", allowBooking: false },
];

const pickPlacementRuleMessage = (rule, lang) => {
  if (!rule) return "";
  if (lang?.startsWith("ar")) return rule.studentMessageAr || "";
  if (lang?.startsWith("de")) return rule.studentMessageDe || "";
  return rule.studentMessageEn || rule.studentMessage || rule.studentMessageDe || "";
};

const getActivePlacementRule = (rules, score, resultStatus) => {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return (
    rules.find((rule) => Number(score) >= Number(rule.minScore) && Number(score) <= Number(rule.maxScore)) ||
    rules.find((rule) => rule.resultStatus === resultStatus) ||
    null
  );
};

const isCatalogEntry = (value) =>
  value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (value.description || value.learnPoints || value.studyPlan);

const getBaseLevel = (value) => {
  if (!value) return "";
  return String(value).trim().match(/^[A-C][0-2]/i)?.[0]?.toUpperCase() || "";
};

const uniqueValues = (items) =>
  items.filter((item, index, array) => item && array.indexOf(item) === index);

const buildParentCatalogEntry = (catalog, parentLevel) => {
  if (!catalog || !parentLevel) return null;

  const children = Object.entries(catalog)
    .filter(([key, value]) => key.startsWith(`${parentLevel}.`) && isCatalogEntry(value))
    .sort(([left], [right]) => left.localeCompare(right, undefined, { numeric: true }))
    .map(([, value]) => value);

  if (!children.length) return null;

  return {
    description: children
      .map((child) => child.description)
      .flat()
      .filter(Boolean),
    learnPoints: children
      .flatMap((child) => child.learnPoints || [])
      .filter(Boolean),
    studyPlan: children.flatMap((child) => child.studyPlan || []).filter(Boolean),
  };
};

const staticTeamImages = [
  { name: "Mafdy Farouk", image: "/team-mafdy-farouk.jpg" },
  { name: "Mohamed Abdulsattar", image: "/team-mohamed-abdulsattar.jpg" },
  { name: "Miriam Dirnberger", image: "/team-miriam-dirnberger.jpg" },
  { name: "Sara Elrefaey", image: "/team-sara-elrefaey.jpg" },
  { name: "Erik Lutz", image: "/team-erik-lutz.jpeg" },
  { name: "Hoda Mostafa", image: "/team-hoda-mostafa.jpg" },
  { name: "Fatema Alzahraa", image: "/team-fatema-alzahraa.JPG" },
  { name: "Yara Ahmed", image: "/team-yara-ahmed.JPG" },
];

const getTeacherImageSrc = (value) => {
  if (!value) return "";
  const src = String(value).trim();
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_API_URL}${src}`;
  if (/^(https?:)?\/\//i.test(src) || src.startsWith("/")) return src;
  return `/${src}`;
};

const CourseDetailsPage = () => {
  const dispatch = useDispatch();
  const { teacherCourseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { courseDetails, loading, error } = useSelector(
    (state) => state.course
  );
  const { status, statusLoading } = useSelector((s) => s.studentPlacement);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [modalOpen, setModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [teacherImageFailed, setTeacherImageFailed] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [bookingTermsChecks, setBookingTermsChecks] = useState({
    termsAndConditionsAccepted: false,
    courseRulesAccepted: false,
  });

  const [expandedPanel, setExpandedPanel] = useState(false);
  const [guestPrice, setGuestPrice] = useState(null);

  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const language = i18n.language?.split("-")[0];
  const currentTermsContent = courseTermsContent[language] || courseTermsContent.en;
  const bookingTermsComplete =
    bookingTermsChecks.termsAndConditionsAccepted &&
    bookingTermsChecks.courseRulesAccepted;
  const bookingTermsAcceptance = {
    ...bookingTermsChecks,
    placementLevelConfirmed: false,
    termsVersion: COURSE_TERMS_VERSION,
  };
  const joinNowButtonSx = {
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "1.5rem",
    py: 1,
    backgroundColor: "#FFE27A",
    border: "2px solid #F0C043",
    color: "#111",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#F6D35F",
    },
    "&:disabled": {
      backgroundColor: "#FFE27A",
      color: "#111",
      opacity: 0.7,
    },
  };

  const {
    courseTitle,
    parentTitle,
    price,
    priceCurrency,
    teacherFirstName,
    teacherLastName,
    teacherId,
    teacherProfilePicture,
    startDate,
    endDate,
    duration,
    dayTimes,
    enrolledStudents,
    maxStudents,
    requiredLevelText,
    placementRequired,
    placementTargetLevel,
    contentEn,
    contentDe,
    contentAr,
    minimumAge,
    maximumAge,
    audienceType,
  } = courseDetails;
  const displayedPrice = isAuthenticated ? price : guestPrice?.amount;
  const displayedCurrency = isAuthenticated ? priceCurrency : guestPrice?.currency;

  useEffect(() => {
    let active = true;
    if (isAuthenticated || !courseDetails.courseId) {
      setGuestPrice(null);
      return () => { active = false; };
    }
    api.get(`/courses/teacher-course/${teacherCourseId}/public-price`)
      .then((response) => {
        if (active) setGuestPrice(response.data);
      })
      .catch(() => {
        if (active) setGuestPrice(null);
      });
    return () => { active = false; };
  }, [courseDetails.courseId, isAuthenticated, teacherCourseId]);

  const courseCatalog =
    i18n.getResourceBundle(i18n.resolvedLanguage || i18n.language, "ui")?.courses ||
    i18n.getResourceBundle("en", "ui")?.courses ||
    {};
  const baseLevel = getBaseLevel(parentTitle || courseTitle);
  const courseKeys = uniqueValues([courseTitle, parentTitle, baseLevel]);
  const staticCourse =
    courseKeys.map((key) => courseCatalog[key]).find(isCatalogEntry) ||
    buildParentCatalogEntry(courseCatalog, baseLevel) ||
    {};
  const customizedContent = isArabic ? contentAr : language === "de" ? contentDe : contentEn;
  const course = customizedContent ? { ...staticCourse, description: customizedContent } : staticCourse;
  const effectivePlacementLevel = placementTargetLevel || baseLevel;
  const courseNeedsPlacement = placementRequired === true || (placementRequired == null && baseLevel && baseLevel !== "A1");

  const descriptionParas = Array.isArray(course?.description)
    ? course.description
    : String(course?.description || "")
        .split(/\n\s*\n/)
        .filter(Boolean);
  const customizedContentItems = customizedContent
    ? String(customizedContent)
        .split(/(?:\r?\n|,|،)/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  const learnPoints = !customizedContent && Array.isArray(course?.learnPoints)
    ? course.learnPoints
    : [];
  const plan = !customizedContent && Array.isArray(course?.studyPlan) ? course.studyPlan : [];
  const courseContentSections = !customizedContent && Array.isArray(course?.courseContent)
    ? course.courseContent
    : [];
  const courseRequirementSections = !customizedContent && Array.isArray(course?.courseRequirements)
    ? course.courseRequirements
    : [];
  const teacherName = `${teacherFirstName || ""} ${teacherLastName || ""}`.trim();
  const fallbackTeacherImage = staticTeamImages.find(
    (member) => member.name.toLowerCase() === teacherName.toLowerCase()
  )?.image;
  const teacherPhotoSrc = getTeacherImageSrc(teacherProfilePicture) || fallbackTeacherImage;
  const hasTeacherPhoto = Boolean(teacherPhotoSrc) && !teacherImageFailed;
  const pickPlacementMessage = () => {
    if (!status) return "";
    if (i18n.language?.startsWith("ar")) return status.studentMessageAr || "";
    if (i18n.language?.startsWith("de")) return status.studentMessageDe || "";
    return status.studentMessageEn || status.studentMessage || status.studentMessageDe || "";
  };
  const placementStatusMatchesCourse =
    !effectivePlacementLevel || !status?.targetLevel || status.targetLevel === effectivePlacementLevel;
  const placementRecommendedLevel = status?.finalLevel || status?.targetLevel || effectivePlacementLevel || "A1";
  const placementRecommendedCoursesUrl = `/german-language?level=${encodeURIComponent(
    placementRecommendedLevel
  )}#courses-section`;
  const placementRules = [...(status?.evaluationRules?.length ? status.evaluationRules : fallbackPlacementRules)]
    .sort((a, b) => Number(b.minScore) - Number(a.minScore));
  const placementScore = Number.isFinite(Number(status?.normalizedScore))
    ? Number(status.normalizedScore)
    : 0;
  const activePlacementRule = getActivePlacementRule(
    placementRules,
    placementScore,
    status?.resultStatus
  );
  const activePlacementMessage =
    pickPlacementMessage() ||
    pickPlacementRuleMessage(activePlacementRule, i18n.language) ||
    t("courseDetails.placementTest.resultFallback", {
      defaultValue: "Your placement result is ready.",
    });
  const getPlacementStatusTitle = (statusKey) =>
    t(`placementStatus.resultCard.statusTitle.${statusKey}`, {
      defaultValue:
        statusKey ||
        t("placementStatus.resultCard.placementResult", {
          defaultValue: "Placement result",
        }),
    });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    if (teacherCourseId) {
      dispatch(fetchCourseDetails(teacherCourseId));
    }
  }, [dispatch, teacherCourseId]);

  useEffect(() => {
    setTeacherImageFailed(false);
  }, [teacherProfilePicture]);

  useEffect(() => {
    const hasGuestPlacementToken = Boolean(localStorage.getItem("placementGuestToken"));
    if (courseNeedsPlacement && effectivePlacementLevel && (isAuthenticated || hasGuestPlacementToken)) {
      dispatch(fetchPlacementTestStatus(effectivePlacementLevel));
    }
  }, [courseNeedsPlacement, dispatch, effectivePlacementLevel, isAuthenticated]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get("status");
    if (status === "success" || status === "failure") {
      setBookingStatus(status);
      setBookingModalOpen(true);
    }
  }, [location.search]);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };
  const openModalWithPlacementCheck = async (setter) => {
    if (statusLoading) {
      return;
    }

    const hasGuestPlacementToken = Boolean(localStorage.getItem("placementGuestToken"));

    if (courseNeedsPlacement && effectivePlacementLevel && (isAuthenticated || hasGuestPlacementToken)) {
      await dispatch(fetchPlacementTestStatus(effectivePlacementLevel));
    }

    setter(true);
  };

  const handleOpenJoinNowModal = () =>
    openModalWithPlacementCheck(setModalOpen);

  const handleBookingTermCheckChange = (field) => (event) => {
    setBookingTermsChecks((current) => ({
      ...current,
      [field]: event.target.checked,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography color="error">
          {t("errorLoadingCourse") || "Error loading course details."}
        </Typography>
      </Box>
    );
  }

  if (!courseDetails || !courseDetails.courseTitle) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography>{t("noCourseFound") || "Course not found."}</Typography>
      </Box>
    );
  }

  // Format dates
  const formattedStartDate = startDate
    ? new Date(startDate).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  const formattedEndDate = endDate
    ? new Date(endDate).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  // Format time as "hh:mm AM/PM" and remove seconds if present
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const date = new Date();
    date.setHours(Number(h), Number(m), 0);
    return date.toLocaleTimeString(isArabic ? "ar-EG" : "en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const scheduleTimes = Array.isArray(dayTimes)
    ? dayTimes
        .filter((dt) => dt?.dayOfWeek && dt?.startTime && dt?.endTime)
        .map((dt) => ({
          dayOfWeek: dt.dayOfWeek,
          startTime: dt.startTime,
          endTime: dt.endTime,
        }))
    : [];

  const formattedScheduleTimes = scheduleTimes.map((dt) => ({
    ...dt,
    startTime: formatTime(dt.startTime),
    endTime: formatTime(dt.endTime),
  }));

  const metaTitle = courseTitle
    ? `${courseTitle} | ALGermanist Course Details`
    : "Course Details | ALGermanist";
  const metaDescription = courseTitle
    ? `Discover details for ${courseTitle}: schedule, study plan, teacher and pricing.`
    : "Explore German course details including schedule, study plan, and teacher.";
  const metaKeywords = `German course, language learning, study plan, ALGermanist${
    courseTitle ? ", " + courseTitle : ""
  }`;

  return (
    <>
      <Meta
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
      />
      <Box
        dir={isArabic ? "rtl" : "ltr"}
        sx={{
          background: "#F6F8F8",
          direction: isArabic ? "rtl" : "ltr",
          textAlign: isArabic ? "right" : "left",
          "& .MuiAccordionSummary-content": {
            textAlign: isArabic ? "right" : "left",
          },
          "& .MuiAccordionDetails-root": {
            textAlign: isArabic ? "right" : "left",
          },
          "& .MuiAlert-message": {
            textAlign: isArabic ? "right" : "left",
          },
        }}
      >
        {/*Course Details Section */}
        <Box
          sx={{
            m: { xs: 2, sm: 3, md: 4 },
            background: "#FFFFFF",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            borderRadius: "12px",
            mb: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
            minHeight: { xs: "auto", md: 250 },
            alignItems: "stretch",
            justifyContent: "space-between",
            gap: { xs: 1, md: 3 },
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Course intro, action, and info */}
          <Box
            sx={{
              flex: "1 1 100%",
              px: { xs: 1, sm: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              mb={0}
              sx={{ fontSize: { xs: "1.4rem", sm: "1.6rem", md: "2rem" } }}
            >
              {courseTitle
                ? t("courseDetails.title", { courseTitle })
                : t("courseDetails.defaultTitle")}
            </Typography>
            <CourseLevelCoverage level={parentTitle || courseTitle} />
            </Box>
            <Box
              sx={{
                mt: { xs: 2.5, md: 3 },
                width: "100%",
              }}
            >
              <CourseInfoBox
                compact
                dayTimes={formattedScheduleTimes}
                formattedStartDate={formattedStartDate}
                formattedEndDate={formattedEndDate}
                duration={duration}
                enrolledStudents={enrolledStudents}
                maxStudents={maxStudents}
                courseLevel={baseLevel}
                requiredLevelText={requiredLevelText}
                audienceType={audienceType}
                minimumAge={minimumAge}
                maximumAge={maximumAge}
                specialCourseCustomization={Boolean(customizedContent)}
                teacherName={teacherName}
                teacherPhotoSrc={teacherPhotoSrc}
                hasTeacherPhoto={hasTeacherPhoto}
                teacherFirstName={teacherFirstName}
                teacherLastName={teacherLastName}
                onTeacherClick={() => teacherId && navigate(`/teachers/${teacherId}`)}
                onTeacherImageError={(event) => {
                  if (
                    fallbackTeacherImage &&
                    !event.currentTarget.src.endsWith(fallbackTeacherImage)
                  ) {
                    event.currentTarget.src = fallbackTeacherImage;
                    return;
                  }
                  setTeacherImageFailed(true);
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            m: { xs: 2, sm: 3, md: 4 },
            mt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Accordion
            expanded={expandedPanel === "content"}
            onChange={handleAccordionChange("content")}
            disableGutters
            sx={{
              borderRadius: "12px !important",
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowDownRoundedIcon />}
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1.25 },
                "& .MuiAccordionSummary-content": { my: 1 },
              }}
            >
              <Typography
                fontWeight={800}
                sx={{ fontSize: { xs: "1rem", sm: "1.2rem", md: "1.35rem" } }}
              >
                {t("courseDetails.tabs.courseContent")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
              {courseContentSections.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {courseContentSections.map((section, sectionIndex) => (
                    <Box key={sectionIndex}>
                      <Typography
                        fontWeight={800}
                        sx={{
                          mb: 1.5,
                          color: "#111",
                          fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {(section.items || []).map((item, index) => (
                          <Box
                            key={index}
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: "#FFD600",
                                flexShrink: 0,
                                mt: "0.55em",
                              }}
                            />
                            <Typography
                              sx={{
                                color: "#617A79",
                                lineHeight: 1.8,
                                fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" },
                              }}
                            >
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <>
                  {customizedContent ? (
                    <Box
                      component="ul"
                      dir={isArabic ? "rtl" : "ltr"}
                      sx={{
                        m: 0,
                        ps: 3,
                        color: "#617A79",
                        textAlign: isArabic ? "right" : "left",
                        listStylePosition: "inside",
                      }}
                    >
                      {customizedContentItems.map((item, idx) => (
                        <Typography
                          component="li"
                          key={`${item}-${idx}`}
                          variant="body1"
                          sx={{
                            lineHeight: 1.9,
                            mb: 1,
                            fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" },
                            textAlign: isArabic ? "right" : "left",
                            direction: isArabic ? "rtl" : "ltr",
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  ) : descriptionParas.map((p, idx) => (
                    <Typography key={idx} variant="body1" sx={{ lineHeight: 1.9, mb: 2, fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" }, color: "#617A79" }}>
                      {p}
                    </Typography>
                  ))}

                  {learnPoints.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                      {learnPoints.map((item, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: "#FFD600",
                              flexShrink: 0,
                              mt: "0.45em",
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              lineHeight: 1.8,
                              fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" },
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 2,
                    }}
                  >
                    {plan.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          background: "#F6F8F8",
                          borderRadius: "10px",
                          p: { xs: 2, sm: 2.5 },
                          border: "1px solid #E7ECEC",
                        }}
                      >
                        <Typography
                          fontWeight={800}
                          sx={{
                            mb: 1,
                            color: "#111",
                            fontSize: { xs: "1rem", sm: "1.08rem" },
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#617A79",
                            lineHeight: 1.75,
                            fontSize: { xs: "0.92rem", sm: "1rem" },
                          }}
                        >
                          {item.summary}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {!customizedContent && <Accordion
            expanded={expandedPanel === "requirements"}
            onChange={handleAccordionChange("requirements")}
            disableGutters
            sx={{
              borderRadius: "12px !important",
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<KeyboardArrowDownRoundedIcon />}
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1.25 },
                "& .MuiAccordionSummary-content": { my: 1 },
              }}
            >
              <Typography
                fontWeight={800}
                sx={{ fontSize: { xs: "1rem", sm: "1.2rem", md: "1.35rem" } }}
              >
                {t("courseDetails.tabs.courseRequirements")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
              {courseRequirementSections.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {courseRequirementSections.map((section, sectionIndex) => (
                    <Box key={sectionIndex}>
                      <Typography
                        fontWeight={800}
                        sx={{
                          mb: 1.5,
                          color: "#111",
                          fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {(section.items || []).map((item, index) => (
                          <Box
                            key={index}
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: "#FFD600",
                                flexShrink: 0,
                                mt: "0.55em",
                              }}
                            />
                            <Typography
                              sx={{
                                color: "#617A79",
                                lineHeight: 1.8,
                                fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" },
                              }}
                            >
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "#617A79",
                    lineHeight: 1.9,
                    fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.12rem" },
                  }}
                >
                  {t("courseDetails.requirementsPlaceholder")}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>}

          {courseNeedsPlacement && effectivePlacementLevel && (
            <Accordion
              expanded={expandedPanel === "placement"}
              onChange={handleAccordionChange("placement")}
              disableGutters
              sx={{
                borderRadius: "12px !important",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<KeyboardArrowDownRoundedIcon />}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.75, sm: 1.25 },
                  "& .MuiAccordionSummary-content": { my: 1 },
                }}
              >
                <Typography
                  fontWeight={800}
                  sx={{ fontSize: { xs: "1rem", sm: "1.2rem", md: "1.35rem" } }}
                >
                  {t("courseDetails.tabs.placementTest", { defaultValue: "Placement Test" })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#FFF9E6",
                    border: "1px solid rgba(255, 214, 0, 0.45)",
                  }}
                >
                  <Box>
                    <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                      {t("courseDetails.placementTest.title", {
                        level: effectivePlacementLevel || "A2",
                        defaultValue: "Placement test required for {{level}}",
                      })}
                    </Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {t("courseDetails.placementTest.description", {
                        defaultValue:
                          "Before booking this level, please complete the placement test so we can confirm the right course level for you.",
                      })}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() =>
                      navigate(
                        `/placement/rules?targetLevel=${encodeURIComponent(
                          effectivePlacementLevel || "A2"
                        )}`
                      )
                    }
                    sx={{
                      bgcolor: "#FFD600",
                      color: "#111",
                      fontWeight: 900,
                      borderRadius: 999,
                      px: 3,
                      whiteSpace: "nowrap",
                      "&:hover": { bgcolor: "#f5c800" },
                    }}
                  >
                    {t("courseDetails.placementTest.cta", {
                      defaultValue: "Start placement test",
                    })}
                  </Button>
                </Box>
                {status?.finalized && placementStatusMatchesCourse && (
                  <Box
                    sx={{
                      mt: 2,
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      bgcolor: "#fff",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 900 }}>
                        {t("courseDetails.placementTest.resultTitle", {
                          defaultValue: "Your latest placement result",
                        })}
                      </Typography>
                      {status.targetLevel && (
                        <Chip
                          size="small"
                          label={`${t("placementStatus.resultCard.targetLabel", { defaultValue: "Target" })}: ${status.targetLevel}`}
                        />
                      )}
                    </Stack>

                    <Box
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, rgba(255,193,7,0.18), rgba(255,255,255,1))",
                        border: "1px solid rgba(245, 158, 11, 0.35)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Box
                          sx={{
                            width: { xs: 76, sm: 88 },
                            height: { xs: 76, sm: 88 },
                            borderRadius: "50%",
                            bgcolor: "#ffc107",
                            color: "#111827",
                            display: "grid",
                            placeItems: "center",
                            fontWeight: 900,
                            fontSize: { xs: 22, sm: 26 },
                            boxShadow: "0 10px 24px rgba(245, 158, 11, 0.35)",
                            flex: "0 0 auto",
                          }}
                        >
                          {placementScore}
                        </Box>
                        <Box flex={1} sx={{ width: "100%" }}>
                          <Typography variant="overline" color="text.secondary">
                            {activePlacementRule?.minScore ?? 0}–
                            {activePlacementRule?.maxScore ?? 100}{" "}
                            {t("placementStatus.resultCard.pointsLabel", { defaultValue: "points" })}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                            {getPlacementStatusTitle(activePlacementRule?.resultStatus)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7 }}>
                            {activePlacementMessage}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate(placementRecommendedCoursesUrl)}
                            sx={{
                              mt: 1.5,
                              bgcolor: "#ffc107",
                              color: "#111827",
                              fontWeight: 900,
                              textTransform: "none",
                              borderRadius: 999,
                              width: { xs: "100%", sm: "auto" },
                              "&:hover": { bgcolor: "#f5b700" },
                            }}
                          >
                            {t("courseDetails.placementTest.recommendedCoursesCta", {
                              level: placementRecommendedLevel,
                              defaultValue: "View recommended courses",
                            })}
                          </Button>
                        </Box>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, placementScore))}
                        sx={{
                          mt: 2,
                          height: 9,
                          borderRadius: 99,
                          bgcolor: "rgba(17, 24, 39, 0.08)",
                          "& .MuiLinearProgress-bar": { bgcolor: "#ffc107" },
                        }}
                      />
                    </Box>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {placementRules.map((rule) => {
                        const isActive = activePlacementRule === rule;
                        return (
                          <Box
                            key={`${rule.minScore}-${rule.maxScore}`}
                            sx={{
                              p: { xs: 1.25, sm: 1.5 },
                              borderRadius: 2,
                              border: isActive ? "2px solid #ffc107" : "1px solid #e5e7eb",
                              bgcolor: isActive ? "#fff8db" : "#fafafa",
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.5}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                            >
                              <Chip
                                size="small"
                                label={`${rule.minScore}–${rule.maxScore} ${t("placementStatus.resultCard.pointsLabel", { defaultValue: "points" })}`}
                                color={isActive ? "warning" : "default"}
                                sx={{ fontWeight: 800 }}
                              />
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                  {getPlacementStatusTitle(rule.resultStatus)}
                                  {isActive
                                    ? ` · ${t("placementStatus.resultCard.yourRange", { defaultValue: "Your range" })}`
                                    : ""}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                  {pickPlacementRuleMessage(rule, i18n.language)}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>

                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                      {status.finalLevel && (
                        <Chip
                          size="small"
                          label={`${t("placementStatus.resultCard.finalLevelLabel", { defaultValue: "Final level" })}: ${status.finalLevel}`}
                          variant="outlined"
                        />
                      )}
                      {status.allowBooking !== null && status.allowBooking !== undefined && (
                        <Chip
                          size="small"
                          label={
                            status.allowBooking
                              ? t("placementStatus.resultCard.bookingAllowed", { defaultValue: "Booking allowed" })
                              : t("placementStatus.resultCard.reviewRecommendedFirst", { defaultValue: "Review recommended first" })
                          }
                          color={status.allowBooking ? "success" : "warning"}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          <Paper
            elevation={0}
            sx={{
              mt: { xs: 2.5, md: 3 },
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: bookingTermsComplete ? "1px solid #D1FADF" : "1px solid #FDE68A",
              bgcolor: bookingTermsComplete ? "#F6FEF9" : "#FFFCF0",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box>
                <Typography fontWeight={900}>
                  {t("joinNowContent.payment.terms.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bookingTermsComplete
                    ? t("joinNowContent.payment.terms.complete")
                    : t("joinNowContent.payment.terms.required")}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setTermsModalOpen(true)}
                sx={{
                  borderColor: "#D6A800",
                  color: "#111",
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: "#B38B00",
                    bgcolor: "#FFF7CC",
                  },
                }}
              >
                {t("joinNowContent.payment.terms.readButton")}
              </Button>
            </Box>

            <Stack sx={{ mt: 1.5 }} spacing={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingTermsChecks.termsAndConditionsAccepted}
                    onChange={handleBookingTermCheckChange("termsAndConditionsAccepted")}
                  />
                }
                label={t("joinNowContent.payment.terms.checkTerms")}
                sx={{ alignItems: "flex-start", m: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookingTermsChecks.courseRulesAccepted}
                    onChange={handleBookingTermCheckChange("courseRulesAccepted")}
                  />
                }
                label={t("joinNowContent.payment.terms.checkRules")}
                sx={{ alignItems: "flex-start", m: 0 }}
              />
            </Stack>
          </Paper>

          {displayedPrice != null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "center",
                width: { xs: "100%", sm: 420 },
                mt: { xs: 1.5, md: 2 },
                py: { xs: 1.5, sm: 1.75 },
                px: { xs: 2, sm: 3 },
                direction: isArabic ? "rtl" : "ltr",
                borderRadius: "14px",
                backgroundColor: "#FFF9E6",
                boxShadow: "0 6px 18px rgba(217, 154, 0, 0.14)",
                border: "1px solid #F5D77A",
                borderTop: "4px solid #F4B400",
              }}
            >
              <Typography
                fontWeight={800}
                textAlign="center"
                sx={{
                  fontSize: { xs: "1.65rem", sm: "2rem" },
                  lineHeight: 1.1,
                  color: "#B77900",
                }}
              >
                {displayedCurrency} {displayedPrice}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 1.5, md: 2 },
            }}
          >
            <Button
              variant="contained"
              disabled={statusLoading || !bookingTermsComplete}
              onClick={handleOpenJoinNowModal}
              sx={{
                ...joinNowButtonSx,
                width: { xs: "100%", sm: 420 },
                maxWidth: "100%",
              }}
            >
              {t("courseDetails.joinNow", { courseTitle })}
              {statusLoading && (
                <Box
                  component="span"
                  sx={{ ml: 2, display: "inline-flex", alignItems: "center" }}
                >
                  <LoadingSpinner size={24} />
                </Box>
              )}{" "}
            </Button>
          </Box>

          <TeacherFeedbackSection teacherId={teacherId} teacherName={teacherName} placement="course" />

        </Box>

        <JoinNowModalManager
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          isAuthenticated={isAuthenticated}
          parentTitle={parentTitle}
          courseTitle={courseTitle}
          teacherCourseId={courseDetails.teacherCourseId}
          priceCurrency={priceCurrency}
          placementRequired={courseNeedsPlacement}
          placementTargetLevel={effectivePlacementLevel}
          termsAcceptance={bookingTermsAcceptance}
          termsAcceptanceComplete={bookingTermsComplete}
        />
        <Dialog
          open={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, direction: isArabic ? "rtl" : "ltr" } }}
        >
          <DialogTitle sx={{ fontWeight: 900 }}>
            {currentTermsContent.title}
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: { xs: "70vh", md: "72vh" } }}>
            <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
              {t("joinNowContent.payment.terms.modal.intro")}
            </Typography>
            <Typography
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "#344054",
                fontFamily: "inherit",
                fontSize: { xs: "0.9rem", md: "0.98rem" },
                lineHeight: 1.75,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {currentTermsContent.body}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="contained"
              onClick={() => setTermsModalOpen(false)}
              sx={{
                bgcolor: "#F2C200",
                color: "#111",
                fontWeight: 900,
                textTransform: "none",
                borderRadius: 2,
                "&:hover": { bgcolor: "#E0B400" },
              }}
            >
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </DialogActions>
        </Dialog>
        <BookingConfirmationModal
          open={bookingModalOpen}
          onClose={() => {
            setBookingModalOpen(false);
            // Optional: remove query params from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, "", newUrl);
          }}
          status={bookingStatus}
          courseTitle={courseTitle}
          teacherName={teacherName}
        />
      </Box>
    </>
  );
};

export default CourseDetailsPage;
